'use client';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { CompanySlider } from '@/services/dataTypes';
import { getBaseImageUrl } from '@/constants/hooks';

interface Props {
  sliders: CompanySlider[];
  domain: string;
}

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const SingleBannerSlider = ({ sliders, domain }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1 },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
  });

  const startAutoplay = () => {
    if (timerRef.current || sliders.length <= 1) return;
    timerRef.current = setInterval(() => slider.current?.next(), 4500);
  };

  const stopAutoplay = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [slider, sliders.length]);

  /* ── Single slide (no carousel needed) ── */
  if (sliders.length === 1) {
    const slide = sliders[0];
    return (
      <div className="relative w-full overflow-hidden rounded-2xl shadow-sm border border-gray-100">
        <div className="relative w-full bg-white">
          <Image
            src={getBaseImageUrl(domain, slide.slider_image, '')}
            alt="Banner"
            width={1400}
            height={420}
            priority
            className={`w-full h-auto object-cover rounded-2xl duration-500`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>
      </div>
    );
  }

  /* ── Multi-slide carousel ── */
  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl shadow-sm border border-gray-100 group"
      onMouseEnter={stopAutoplay}
      onMouseLeave={startAutoplay}
    >
      <div ref={sliderRef} className="keen-slider">
        {sliders.map((slide, i) => (
          <div key={i} className="keen-slider__slide relative bg-white">
            <div className="relative w-full bg-white">
              <Image
                src={getBaseImageUrl(domain, slide.slider_image, '')}
                alt={`Banner ${i + 1}`}
                width={1400}
                height={420}
                priority={i === 0}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Overlay CTA */}
            {(slide.text || (slide.button_text && slide.button_link)) && (
              <div
                className="absolute inset-0 flex flex-col items-start justify-center px-8 md:px-14 gap-4"
                style={{ background: 'linear-gradient(90deg, rgba(71,150,105,0.82) 0%, transparent 70%)' }}
              >
                {slide.text && (
                  <h2 className="text-white font-black text-2xl md:text-4xl max-w-md leading-tight drop-shadow-lg">
                    {slide.text}
                  </h2>
                )}
                {slide.button_text && slide.button_link && (
                  <Link
                    href={slide.button_link}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg no-underline"
                    style={{ background: '#479669', color: '#ffffff', boxShadow: '0 4px 16px rgba(71,150,105,0.35)' }}
                  >
                    {slide.button_text}
                    <ChevronRight />
                  </Link>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Prev / Next arrows */}
      <button
        onClick={() => slider.current?.prev()}
        aria-label="Previous slide"
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        style={{ background: 'rgba(255,255,255,0.92)', border: '1px solid rgba(71,150,105,0.3)', color: '#479669', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <ChevronLeft />
      </button>
      <button
        onClick={() => slider.current?.next()}
        aria-label="Next slide"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        style={{ background: 'rgba(255,255,255,0.92)', border: '1px solid rgba(71,150,105,0.3)', color: '#479669', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <ChevronRight />
      </button>

      {/* Dot indicators */}
      {sliders.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {sliders.map((_, i) => (
            <button
              key={i}
              onClick={() => slider.current?.moveToIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === currentSlide ? '24px' : '8px',
                height: '8px',
                background: i === currentSlide ? '#479669' : 'rgba(255,255,255,0.6)',
                boxShadow: i === currentSlide ? '0 0 8px rgba(71,150,105,0.5)' : 'none',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SingleBannerSlider;
